/**
 * `/segments/:segmentId` — one segment, and what is actually in it.
 *
 * The counts lead, and they are explained rather than displayed. Four bare
 * numbers would leave a person to subtract two of them and guess why they
 * differ; the panel says it out loud — this segment holds 1,287 rows, they are
 * 1,278 different people, and a campaign reaches each person once. The gap has
 * to read as precision, because it is: the same person (`person_id`, since slice
 * 58) the send path reaches once is what produced it.
 *
 * Everything else on the screen exists to make those numbers believable. On a
 * static segment the member lists are shown with their own live contact counts, so
 * a member sitting at zero can be seen to be a list that has not finished
 * importing rather than a miscount — a cohort has no member lists and no such card
 * (see the slice 50 section below). The contact preview is the rows themselves,
 * paginated and searchable, whichever kind this is, because "1,278 recipients" is
 * a claim and this is the evidence.
 *
 * Nothing on this page is cached. Every number came from this request, and a
 * membership change re-reads them rather than adjusting them locally — a segment
 * references its lists, so its counts are only ever the answer to a question
 * asked just now.
 *
 * ## Which sentences were allowed to get shorter, and which were not
 *
 * Almost every card here opened by re-arguing the paragraph above — that a
 * segment points at its lists rather than copying them — and then said what the
 * card contained. The argument is reasoning for whoever changes this screen and
 * it is now only here. Four statements stayed on the surface, at the length
 * they need to be understood in one pass:
 *
 * - **The three counts stay three.** `SegmentCountsPanel` owns them and is not
 *   touched by this slice: rows, unique recipients, and reach per channel as
 *   *distinct addresses*. This screen never collapses them into a single "size"
 *   and never puts a reach figure over the row count.
 * - **The contacts table is rows, not recipients.** Somebody on two member
 *   lists appears twice in it. Without that said next to the table, the table
 *   looks like it contradicts the recipient count directly above it, and the
 *   number people trust in that situation is the bigger one.
 * - **A member at zero is explained where it is shown.** "None yet" beside an
 *   importing list is the honest reading of a count that is not measured yet,
 *   and it is why the status badge travels with the figure.
 * - **Delete says what it destroys.** What is lost is the grouping and the
 *   name; the lists, their contacts and every other segment using them are
 *   untouched. It cannot be undone, and that is stated as plainly as the
 *   reassurance is — the reassurance without it would read as an undo.
 *
 * ## Slice 50: two kinds of segment, and the screen is not the same for both
 *
 * A **cohort** is who one Overview count counted, on one day. It combines no
 * uploads and its membership cannot be edited person by person, so three things
 * change and every one of them is a claim this screen would otherwise make
 * falsely:
 *
 * - The meta line's first label is the count it came from and the day it was
 *   counted, not `0 lists`. A cohort has no uploads to count, and the age of the
 *   snapshot is the one fact a person about to send cannot work out for
 *   themselves. A `Snapshot` chip sits beside it — in the `ident-a` identity
 *   colour since slice 66, a kind rather than a status; the list screen draws
 *   the identical three labels from `lib/segment-display.ts`.
 * - **"Lists in this segment" is not drawn at all** — not drawn empty. Gated on
 *   `type`, because `datasets: []` is also a brand-new static segment somebody is
 *   about to fill, and the two need opposite screens.
 * - **Delete's reassurance is per kind.** "You can build the same segment again
 *   from them" is true of ticked lists and false of a snapshot, and it was
 *   printed beside the one action that cannot be undone.
 *
 * Everything else — the counts, the contact preview, renaming, describing,
 * deleting — is the same for both, because all of it is true of both.
 *
 * ## Slice 66: the counts card is the Blueprint's result panel and its sample
 *
 * "Who is in this segment" holds two things side by side now. On the left,
 * `SegmentCountsPanel` as a brand `MetricPanel` — the people figure, "from N
 * contacts (rows)", and the two channels as bars out of those people; that
 * component's header says what it refused to port and why. On the right, a
 * sample: the newest five rows, as `PersonRow`s, read with the same request
 * the contacts table makes. It is called a sample of rows because that is
 * what page one of that request is — not "who matches", which is the phase-B
 * rule builder's live count and does not exist here.
 *
 * The sample is a second read on this screen, and it fails on its own: its
 * `ErrorState` replaces the sample, never the counts beside it, which came
 * from the segment read and are still true. Below `lg` the two stack, counts
 * first, because the counts are the claim and the sample is its evidence.
 *
 * The member rows' counts are sans with tabular figures, like every count in
 * this slice; "none yet" stays mono caps because it is a word standing in a
 * figure's place, and it should look like one.
 *
 * ## Slice 67: the third kind, a rule segment
 *
 * Drawn on `type`, like the cohort. A rule segment has no lists, so it gets no
 * Lists card; in its place it gets two of its own — its rules, read-only with
 * an Edit button that opens the builder in place (`RuleSegmentRules`), and the
 * membership timeline of who entered and left, and why (`MembershipTimeline`).
 * Its header says "rules" where a static segment counts lists, and wears the
 * "Dynamic" chip, because its counts are only true as of the last refresh and
 * will move without anyone touching this page. The counts card, the sample and
 * the contact table are unchanged: the API's membership limb for rule segments
 * (`segment_people`) feeds the same reads.
 */
import {
  DEFAULT_PAGE_SIZE,
  MAX_SEGMENT_DESCRIPTION_LENGTH,
  contactSortSchema,
  updateSegmentRequestSchema,
  type Contact,
  type Segment,
  type SegmentDataset,
} from '@wend/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  Kicker,
  PersonList,
  PersonRow,
  Spinner,
  buttonVariants,
  useToast,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import {
  ArrowLeft,
  FileSpreadsheet,
  ListChecks,
  Pencil,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { DatasetStatusBadge } from '../../components/DatasetStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { SegmentCountsPanel } from '../../components/SegmentCounts';
import { Textarea } from '../../components/Textarea';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import {
  deleteSegment,
  fetchSegment,
  listSegmentContacts,
  updateSegment,
} from '../../lib/api/segments';
import { datasetSheetLabel } from '../../lib/dataset-display';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { formatCount, formatDate, formatDateTime, pluralise } from '../../lib/format';
import { cohortProvenance } from '../../lib/segment-display';
import { useAsyncData } from '../../lib/use-async-data';
import { DatasetPicker, contactsPending, membershipError } from './DatasetPicker';
import { MembershipTimeline } from './MembershipTimeline';
import { RuleSegmentRules } from './RuleSegmentRules';

/** Typing pauses this long before the contact preview re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

export function SegmentDetailPage() {
  const { segmentId } = useParams<{ segmentId: string }>();

  // The route always supplies it; this is the type system's question, not a
  // state the app can reach, so it goes back to the list rather than erroring.
  if (segmentId === undefined) return <Navigate to="/segments" replace />;

  return <SegmentDetail segmentId={segmentId} />;
}

function SegmentDetail({ segmentId }: { segmentId: string }) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchSegment(segmentId, signal),
    [segmentId],
  );

  /**
   * The segment as this screen has changed it since the server last spoke.
   * Every edit answers with the whole segment — counts included — so a save
   * never has to guess what it did to the numbers. Cleared whenever a fresh copy
   * arrives, so the server always wins in the end.
   */
  const [patched, setPatched] = useState<Segment | null>(null);
  useEffect(() => {
    setPatched(null);
  }, [data]);

  const segment = patched ?? data;

  if (segment === null) {
    return (
      <AppLayout title="Segment" actions={<BackToSegments />}>
        <Card>
          {loadingFirst ? (
            <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading this segment…</span>
            </CardContent>
          ) : (
            <ErrorState error={error} title="Could not load this segment" onRetry={reload} />
          )}
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={segment.name} actions={<BackToSegments />}>
      <div className="flex flex-col gap-6">
        {error !== null && (
          <FormAlert tone="error">
            {errorMessage(error)} What is shown below is the last version that loaded.
          </FormAlert>
        )}

        <SegmentHeader segment={segment} onSaved={setPatched} />

        {/* Slice 67: a rule segment's rules, read-only with an Edit button that
            opens the builder in place. Above the counts, because the rules are
            what the counts are counts OF. */}
        {segment.type === 'rule' && <RuleSegmentRules segment={segment} onSaved={setPatched} />}

        <Card>
          <CardHeader>
            {/* A caching claim, on the card whose job is to show numbers, and
                the same claim is made twice more on this page. Nothing a sender
                does changes based on it. Slice 29. */}
            <CardTitle>Who is in this segment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-start">
              <SegmentCountsPanel counts={segment.counts} />
              <SegmentSample segment={segment} />
            </div>
          </CardContent>
        </Card>

        {/*
          A COHORT HAS NO LISTS CARD AT ALL, AND NOT BECAUSE IT WOULD BE EMPTY.

          It is drawn on `type`, never on `datasets.length`: a cohort's `datasets`
          is `[]` and so is a brand-new static segment's, and those two are not
          the same thing — one holds people and no uploads, the other is a
          container somebody is about to fill. Drawn for a cohort the card would
          print "No lists in this segment, so it is empty and reports zeros" over
          1,284 people, and offer an "Add lists" button the API refuses with
          `cohort_takes_no_datasets`. A control that cannot work is worse than a
          missing one: the person presses it, reads a refusal, and learns nothing
          about what this segment is.
        */}
        {segment.type === 'static' && <MemberLists segment={segment} onSaved={setPatched} />}

        {/* A rule segment has no lists either — for it this slot is who entered
            and left, and why: the thing that moves while nobody is looking. */}
        {segment.type === 'rule' && (
          <MembershipTimeline segmentId={segment.id} refreshedAt={segment.rulesRefreshedAt} refreshKey={segment.updatedAt} />
        )}

        <SegmentContacts segment={segment} />

        <DangerZone segment={segment} />
      </div>
    </AppLayout>
  );
}

function BackToSegments() {
  return (
    <Link to="/segments" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All segments
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* A sample of who is in it                                                    */
/* -------------------------------------------------------------------------- */

/** How many rows the sample shows. Small on purpose: the table below has the rest. */
const SAMPLE_SIZE = 5;

/**
 * The first few rows, beside the counts — the Blueprint's "sample" under its
 * result panel, so "1,284 people" arrives with faces rather than only a number.
 *
 * **Rows, newest first, exactly as the contacts table below lists them**, read
 * through the same `listSegmentContacts` with a page of five. It is labelled
 * as rows because it is: somebody on two lists can appear twice in it, which
 * is the same sentence the table carries. It is not a random sample and does
 * not say it is; "the newest five rows" is the honest name for page one.
 *
 * Contacts are shown as the table shows them — the email, or the number when
 * there is no email — so this adds no exposure the screen did not already
 * have. Each row's list is named where the segment knows it; a cohort's rows
 * come from no list it holds, so they say when they were added instead.
 *
 * It has its own request and so its own failure: an `ErrorState` with a retry,
 * in place of the sample only, because the counts beside it came from a
 * different read and are still true.
 */
function SegmentSample({ segment }: { segment: Segment }) {
  const memberKey = segment.datasets.map((dataset) => dataset.id).join(',');
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listSegmentContacts(
        segment.id,
        { page: 1, pageSize: SAMPLE_SIZE, sort: 'createdAt', direction: 'desc' },
        signal,
      ),
    [segment.id, memberKey],
  );

  const listName = new Map(segment.datasets.map((dataset) => [dataset.id, dataset.name]));
  const rows = data?.items ?? [];
  const total = data?.pagination.total ?? null;

  return (
    <section aria-labelledby={`sample-${segment.id}`} className="min-w-0">
      <Kicker as="h3" rule id={`sample-${segment.id}`} className="mb-2">
        {total === null || total === 0
          ? 'Sample'
          : `Sample · newest ${formatCount(rows.length)} of ${pluralise(total, 'row')}`}
      </Kicker>

      {loadingFirst && (
        <div className="flex items-center gap-3 py-2 text-[0.86rem] text-muted">
          <Spinner size="sm" label={null} />
          <span role="status">Loading a sample…</span>
        </div>
      )}

      {!loadingFirst && data === null && (
        <ErrorState
          error={error}
          title="Could not load a sample"
          onRetry={reload}
          className="flex flex-col items-start gap-3 py-2"
        />
      )}

      {data !== null && rows.length === 0 && (
        <p className="text-[0.86rem] leading-relaxed text-muted">Nobody to show yet.</p>
      )}

      {rows.length > 0 && (
        <PersonList>
          {rows.map((contact) => {
            const name = contact.name ?? contact.email ?? contact.phoneE164 ?? 'No name in the file';
            // Whichever address is not already standing in for the name.
            const reach =
              contact.name === null
                ? contact.email === null
                  ? null
                  : contact.phoneE164
                : (contact.email ?? contact.phoneE164);
            // The list it came from where the segment knows it; a cohort holds
            // no lists, so its rows say when they were added instead.
            const from = listName.get(contact.datasetId);

            return (
              <PersonRow
                key={contact.id}
                name={name}
                seed={contact.id}
                meta={from ?? `added ${formatDate(contact.createdAt)}`}
                {...(reach === null ? {} : { contact: reach })}
              />
            );
          })}
        </PersonList>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Header — name and description                                               */
/* -------------------------------------------------------------------------- */

function SegmentHeader({
  segment,
  onSaved,
}: {
  segment: Segment;
  onSaved: (segment: Segment) => void;
}) {
  const [editing, setEditing] = useState(false);
  const provenance = cohortProvenance(segment.cohort);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          {/* "Static union" was product-internal taxonomy. There is one kind
              of segment, so the badge distinguished this one from nothing.
              Slice 29.

              SLICE 50 PUT A CHIP BACK, AND IT IS NOT THAT BADGE RETURNING.
              There are two kinds of segment now, and the difference decides what
              the rest of this screen may claim: a static segment is a union of
              uploads that can be changed, a cohort is who was counted on one day
              and can never be edited person by person. The chip is drawn only on
              the cohort, so the absence of it is still the other state.

              A cohort's first label is the count it came from rather than
              `0 lists`. "0 lists" is arithmetic over the wrong field — a cohort
              has no uploads to count — and it is the reading this slice's review
              named on the list screen. The date beside it is labelled `counted`,
              which is the one date on this line a reader cannot infer: a
              three-month-old cohort is a list of who was dormant three months
              ago, and nothing else here would say so.

              AND `created` IS NOT PRINTED BESIDE IT. For a cohort the two are the
              same instant by construction — `countedAt` is stamped by the save's
              own transaction, the one that writes the segment row — so a line
              reading "counted 26 Sep 2026 · created 26 Sep 2026" would be one
              fact under two names, and the reader would go looking for the
              difference. `updated` stays, because renaming a cohort is ordinary
              and moves it. */}
          {provenance !== null && <Badge tone="ident-a">{provenance.chip}</Badge>}
          {segment.type === 'rule' && (
            <Badge tone="brand" dot>
              Dynamic
            </Badge>
          )}
          <span className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
            {segment.type === 'rule'
              ? `rules · created ${formatDate(segment.createdAt)}`
              : provenance === null
              ? `${pluralise(segment.datasets.length, 'list')} · created ${formatDate(segment.createdAt)}`
              : `${provenance.kind} · ${provenance.countedAt}`}{' '}
            · updated {formatDateTime(segment.updatedAt)}
          </span>
        </div>

        {editing ? (
          <CardTitle>Rename this segment</CardTitle>
        ) : (
          <>
            <CardTitle>{segment.name}</CardTitle>
            <CardDescription>
              {segment.description ?? 'No description. It is a good place to say who this is for.'}
            </CardDescription>
          </>
        )}
      </CardHeader>

      <CardContent>
        {editing ? (
          <DetailsForm
            key={segment.updatedAt}
            segment={segment}
            onSaved={(updated) => {
              onSaved(updated);
              setEditing(false);
            }}
            onCancel={() => {
              setEditing(false);
            }}
          />
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditing(true);
            }}
          >
            <Pencil aria-hidden="true" className="size-4" />
            Rename or describe
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function DetailsForm({
  segment,
  onSaved,
  onCancel,
}: {
  segment: Segment;
  onSaved: (segment: Segment) => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(segment.name);
  const [description, setDescription] = useState(segment.description ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    const result = validate(updateSegmentRequestSchema, { name, description });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const updated = await updateSegment(segment.id, result.data);
      toast({ tone: 'success', title: 'Segment updated' });
      onSaved(updated);
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      noValidate
      className="flex max-w-xl flex-col gap-5"
    >
      {errors[ROOT_FIELD] !== undefined && (
        <FormAlert tone="error" requestId={requestId}>
          {errors[ROOT_FIELD]}
        </FormAlert>
      )}

      <FormField label="Name" required error={errors.name}>
        <Input
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setErrors((current) => withoutError(current, 'name'));
          }}
          autoComplete="off"
          disabled={pending}
        />
      </FormField>

      <FormField
        label="Description"
        description="Optional. Clearing it removes the description entirely."
        error={errors.description}
      >
        <Textarea
          rows={3}
          value={description}
          maxLength={MAX_SEGMENT_DESCRIPTION_LENGTH}
          onChange={(event) => {
            setDescription(event.target.value);
            setErrors((current) => withoutError(current, 'description'));
          }}
          disabled={pending}
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={pending}>
          {pending ? 'Saving' : 'Save changes'}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
        {/* Pre-empted a fear nobody holds: no user expects renaming a thing to
            change its membership, and this form contains a name and a
            description. Slice 29. */}
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Member lists                                                                */
/* -------------------------------------------------------------------------- */

function MemberLists({
  segment,
  onSaved,
}: {
  segment: Segment;
  onSaved: (segment: Segment) => void;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lists in this segment</CardTitle>
        {/* Third statement of reference-not-copy on this single page. The card
            title plus the rows beneath it — each with a live contact count and a
            status badge — carry everything a sender needs. Slice 29. */}
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {editing ? (
          <MembershipForm
            key={segment.updatedAt}
            segment={segment}
            onSaved={(updated) => {
              onSaved(updated);
              setEditing(false);
            }}
            onCancel={() => {
              setEditing(false);
            }}
          />
        ) : (
          <>
            {segment.datasets.length === 0 ? (
              <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
                No lists in this segment, so it is empty and reports zeros. Add one and it fills
                immediately — there is nothing to import or rebuild.
              </p>
            ) : (
              <ul className="flex flex-col">
                {segment.datasets.map((dataset) => (
                  <MemberRow key={dataset.id} dataset={dataset} />
                ))}
              </ul>
            )}

            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditing(true);
                }}
              >
                <ListChecks aria-hidden="true" className="size-4" />
                {segment.datasets.length === 0 ? 'Add lists' : 'Change lists'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One member list.
 *
 * `contactCount` is counted live rather than read off the import's own tally,
 * and `status` travels with it — together they are the honest explanation for a
 * member contributing nothing: a list still importing has no contacts yet.
 *
 * "Yet" is the word `contactsPending` exists to get right, and it comes from
 * `DatasetPicker` so that the picker and this row cannot drift. A member whose
 * import *failed* is not waiting for anything, and "none yet" beside a red
 * Failed badge tells somebody to sit and wait for a number that will not
 * arrive until they go and fix the file.
 */
function MemberRow({ dataset }: { dataset: SegmentDataset }) {
  const sheet = datasetSheetLabel(dataset);

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line py-3 first:border-t-0 first:pt-0">
      <div className="min-w-0 flex-1">
        <Link
          to={`/audiences/${dataset.id}`}
          className="font-medium text-text underline-offset-4 hover:underline"
        >
          {dataset.name}
        </Link>
        {sheet !== null && (
          <p className="flex min-w-0 items-center gap-1 font-mono text-[0.7rem] tracking-[0.04em] text-muted">
            <FileSpreadsheet aria-hidden="true" className="size-3 shrink-0" />
            <span className="sr-only">worksheet </span>
            <span className="truncate">{sheet}</span>
          </p>
        )}
      </div>

      <DatasetStatusBadge status={dataset.status} />

      <span className="w-28 text-right text-[0.86rem] tabular-nums text-text">
        {dataset.status === 'ready' ? (
          <>
            {formatCount(dataset.contactCount)}
            <span className="text-muted"> contacts</span>
          </>
        ) : (
          <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
            {contactsPending(dataset.status)}
          </span>
        )}
      </span>
    </li>
  );
}

/** "Adding 2 lists and removing 1" — one clause, correctly cased and joined. */
function describeChange(added: number, removed: number): string {
  const parts: string[] = [];
  if (added > 0) parts.push(`Adding ${pluralise(added, 'list')}`);
  if (removed > 0) {
    parts.push(`${added > 0 ? 'removing' : 'Removing'} ${pluralise(removed, 'list')}`);
  }
  return parts.join(' and ');
}

function MembershipForm({
  segment,
  onSaved,
  onCancel,
}: {
  segment: Segment;
  onSaved: (segment: Segment) => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<SegmentDataset[]>([...segment.datasets]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const currentIds = useMemo(
    () => new Set(segment.datasets.map((dataset) => dataset.id)),
    [segment.datasets],
  );
  const draftIds = useMemo(() => new Set(draft.map((dataset) => dataset.id)), [draft]);

  const added = draft.filter((dataset) => !currentIds.has(dataset.id)).length;
  const removed = segment.datasets.filter((dataset) => !draftIds.has(dataset.id)).length;
  const changed = added > 0 || removed > 0;
  const projectedContacts = draft.reduce((total, dataset) => total + dataset.contactCount, 0);

  async function onSave(): Promise<void> {
    if (pending) return;

    setRequestId(undefined);

    // Membership is a replacement, not a patch: what is ticked is what the
    // segment becomes.
    const result = validate(updateSegmentRequestSchema, {
      datasetIds: draft.map((dataset) => dataset.id),
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const updated = await updateSegment(segment.id, result.data);
      toast({
        tone: 'success',
        title: 'Lists updated',
        description: `${formatCount(updated.counts.uniqueRecipients)} unique recipients now.`,
      });
      onSaved(updated);
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  const selectionError = membershipError(errors);

  return (
    <div className="flex flex-col gap-5">
      {errors[ROOT_FIELD] !== undefined && (
        <FormAlert tone="error" requestId={requestId}>
          {errors[ROOT_FIELD]}
        </FormAlert>
      )}

      {selectionError !== undefined && <FormAlert tone="error">{selectionError}</FormAlert>}

      <DatasetPicker selected={draft} onChange={setDraft} disabled={pending} />

      <div className="rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
        <p className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
          What saving will change
        </p>
        {changed ? (
          <p className="mt-2 max-w-prose text-[0.88rem] leading-relaxed text-muted">
            <span className="text-text">{describeChange(added, removed)}</span>
            {draft.length === 0 ? (
              <>, leaving the segment empty.</>
            ) : (
              <>
                , leaving {pluralise(draft.length, 'list')} holding{' '}
                <span className="text-text">{pluralise(projectedContacts, 'row')}</span> between
                them.
              </>
            )}{' '}
            {/* "Never derived from a row total" was a correctness claim aimed
                at a reviewer. The projection before it — which lists are being
                added and removed, and how many rows they hold — is the sender's
                content and stays. Slice 29. */}
          </p>
        ) : (
          <p className="mt-2 max-w-prose text-[0.88rem] leading-relaxed text-muted">
            Nothing has changed yet. Tick or untick a list and the effect is described here.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          loading={pending}
          disabled={!changed}
          onClick={() => {
            void onSave();
          }}
        >
          {pending ? 'Saving' : 'Save lists'}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The contacts themselves                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The rows behind the counts.
 *
 * This lists **rows**, not recipients: someone on two of the member lists
 * appears twice here, and that is correct — a campaign reaches each person once,
 * by the same person (`person_id`) `uniqueRecipients` was counted with.
 * Saying so is the difference between a person trusting the smaller number and
 * suspecting the bigger one.
 */
function SegmentContacts({ segment }: { segment: Segment }) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  // Changing what is in the segment changes who is in this table, so the
  // membership is part of the query's identity.
  const memberKey = segment.datasets.map((dataset) => dataset.id).join(',');

  const sortField = contactSortSchema.catch('createdAt').parse(sort.key);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listSegmentContacts(
        segment.id,
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [segment.id, memberKey, page, search, sortField, sort.direction],
  );

  const columns = useMemo<Array<DataTableColumn<Contact>>>(
    () => [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        cell: (contact) =>
          contact.name === null ? (
            <span className="text-muted">No name in the file</span>
          ) : (
            <span className="text-text">{contact.name}</span>
          ),
      },
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        cell: (contact) =>
          contact.email === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="font-mono text-[0.8rem] break-all text-muted">{contact.email}</span>
          ),
      },
      {
        key: 'phone',
        header: 'Phone',
        width: '11rem',
        cell: (contact) =>
          contact.phoneE164 === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="font-mono text-[0.8rem] whitespace-nowrap text-muted">
              {contact.phoneE164}
            </span>
          ),
      },
      {
        key: 'createdAt',
        header: 'Added',
        sortable: true,
        width: '10rem',
        cell: (contact) => (
          <span className="whitespace-nowrap text-muted">{formatDateTime(contact.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contacts in this segment</CardTitle>
        <CardDescription>
          Rows, not recipients — somebody on two of these lists appears twice here. The recipient
          count above is the smaller number, and it is the one a campaign sends to.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failedToLoad ? (
          <ErrorState
            error={error}
            title="Could not load the contacts"
            onRetry={reload}
            className="flex flex-col items-start gap-3 py-2"
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search name, email or phone"
                  aria-label="Search contacts in this segment"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the contacts" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'row')}
                </p>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The rows below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Contacts in this segment"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(contact) => contact.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="48rem"
              empty={
                search === '' ? (
                  <p className="max-w-prose text-[0.9rem] text-muted">
                    {segment.datasets.length === 0
                      ? 'No lists are in this segment, so there is nobody in it. Add one above.'
                      : 'The lists in this segment have no contacts yet — most likely their imports have not finished.'}
                  </p>
                ) : (
                  <p className="text-[0.9rem] text-muted">
                    Nobody in this segment matches <span className="text-text">“{search}”</span>.
                  </p>
                )
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Delete                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Deleting, with the confirmation inline rather than in a dialog.
 *
 * What the confirmation has to say is not "are you sure" but *what is lost* —
 * and the answer is: only the segment. A segment owns no contacts, so its lists
 * and their rows survive it untouched, as does every other segment that
 * includes them. Someone who knows that can decide in one step.
 */
function DangerZone({ segment }: { segment: Segment }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );

  async function onDelete(): Promise<void> {
    if (pending) return;

    setFailure(null);
    setPending(true);

    try {
      await deleteSegment(segment.id);
      toast({ tone: 'success', title: 'Segment deleted', description: `${segment.name} is gone.` });
      navigate('/segments', { replace: true });
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
      setPending(false);
    }
  }

  return (
    <Card className="border-bad/25">
      <CardHeader>
        <CardTitle className="text-[1rem]">Delete this segment</CardTitle>
        {/*
          SLICE 50. "It references no lists, so there is nothing else it could
          touch" was drawn for every segment whose `datasets` was empty, and a
          cohort's always is — so the reassurance that fitted an unfilled
          container was being made about 1,284 people. It is still true that
          nothing else is destroyed, and the cohort branch says so about what a
          cohort actually holds: contacts, which are rows in the lists that are
          not being deleted.
        */}
        <CardDescription>
          Deleting removes the grouping and the name, and nothing else.{' '}
          {segment.type === 'cohort'
            ? 'The people it holds are contacts in your lists, and they are untouched.'
            : segment.datasets.length === 0
              ? 'It references no lists, so there is nothing else it could touch.'
              : `The ${pluralise(segment.datasets.length, 'list')} it combines, every contact in them, and any other segment that includes them are untouched.`}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failure !== null && (
          <FormAlert
            tone="error"
            {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
          >
            {failure.message}
          </FormAlert>
        )}

        {confirming ? (
          <div className="flex flex-col gap-3">
            {/*
              "YOU CAN BUILD THE SAME SEGMENT AGAIN FROM THEM" IS FALSE OF A
              COHORT, AND IT IS ATTACHED TO THE ONE ACTION THAT CANNOT BE UNDONE.

              A static segment is a set of ticked lists, so it really can be
              rebuilt. A cohort is who was counted on one day: saving that count
              again tomorrow writes a DIFFERENT population — somebody messaged in
              between has left the dormant set — so the rebuild a reader was
              promised does not exist, and they find that out after pressing. The
              undo claim is the same for both kinds; the sentence after it is not,
              and getting it wrong here is the cheapest way to lose data in this
              product.
            */}
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-text">
              Delete <span className="font-medium">{segment.name}</span>?{' '}
              <span className="font-medium">This cannot be undone.</span>{' '}
              {segment.type === 'cohort'
                ? 'Your contacts do not change, but this snapshot is gone — counting again saves who is in it then, not who was in it here.'
                : 'Your lists and contacts do not change — you can build the same segment again from them.'}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={pending}
                className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
                onClick={() => {
                  void onDelete();
                }}
              >
                <Trash2 aria-hidden="true" className="size-4" />
                {pending ? 'Deleting' : 'Yes, delete this segment'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setConfirming(true);
              }}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Delete segment
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
